import { marketingOverviewFilter } from '@dataTypes/marketing.type'

export const formatDate = (date: string): string => {
  const d = new Date(date)
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const year = d.getFullYear()
  return `${month}-${day}-${year}`
}

/** MM/DD/YYYY (US order, slash separators). */
export const formatDateUS = (date: string): string => {
  const d = new Date(date)
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const year = d.getFullYear()
  return `${month}/${day}/${year}`
}

export const formatDateTime = (date?: string): string => {
  if (!date) return 'N/A'
  const d = new Date(date)
  if (isNaN(d.getTime())) return 'N/A'
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const year = d.getFullYear()
  const hours = d.getHours() % 12 || 12
  const minutes = String(d.getMinutes()).padStart(2, '0')
  const ampm = d.getHours() >= 12 ? 'PM' : 'AM'
  return `${month}-${day}-${year} - ${hours}:${minutes} ${ampm}`
}

export const getDateRange = (
  filter: marketingOverviewFilter,
  baseDate: Date = new Date(),
): {
  startCurrent: Date
  endCurrent: Date
  startPrev: Date
  endPrev: Date
} => {
  const today = baseDate
  const endCurrent = new Date(today)
  endCurrent.setHours(23, 59, 59, 999)

  switch (filter) {
    case marketingOverviewFilter['1D']:
      // Today
      const startCurrent = new Date(today)
      startCurrent.setHours(0, 0, 0, 0)

      // Yesterday
      const yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1)
      const startPrev = new Date(yesterday)
      startPrev.setHours(0, 0, 0, 0)
      const endPrev = new Date(yesterday)
      endPrev.setHours(23, 59, 59, 999)

      return {
        startCurrent,
        endCurrent,
        startPrev,
        endPrev,
      }
    case marketingOverviewFilter['1W']:
      const startCurrentW = new Date(today)
      startCurrentW.setDate(today.getDate() - 6)
      startCurrentW.setHours(0, 0, 0, 0)

      const endPrevW = new Date(startCurrentW)
      endPrevW.setDate(startCurrentW.getDate() - 1)
      endPrevW.setHours(23, 59, 59, 999)
      const startPrevW = new Date(endPrevW)
      startPrevW.setDate(endPrevW.getDate() - 6)
      startPrevW.setHours(0, 0, 0, 0)

      return {
        startCurrent: startCurrentW,
        endCurrent,
        startPrev: startPrevW,
        endPrev: endPrevW,
      }
    case marketingOverviewFilter['1M']:
      const startCurrentM = new Date(today)
      startCurrentM.setMonth(today.getMonth() - 1)
      startCurrentM.setHours(0, 0, 0, 0)

      const endPrevM = new Date(startCurrentM)
      endPrevM.setDate(startCurrentM.getDate() - 1)
      endPrevM.setHours(23, 59, 59, 999)
      const startPrevM = new Date(endPrevM)
      startPrevM.setMonth(endPrevM.getMonth() - 1)
      startPrevM.setHours(0, 0, 0, 0)

      return {
        startCurrent: startCurrentM,
        endCurrent,
        startPrev: startPrevM,
        endPrev: endPrevM,
      }
    case marketingOverviewFilter['1Y']:
      const startCurrentY = new Date(today)
      startCurrentY.setFullYear(today.getFullYear() - 1)
      startCurrentY.setHours(0, 0, 0, 0)

      const endPrevY = new Date(startCurrentY)
      endPrevY.setDate(startCurrentY.getDate() - 1)
      endPrevY.setHours(23, 59, 59, 999)
      const startPrevY = new Date(endPrevY)
      startPrevY.setFullYear(endPrevY.getFullYear() - 1)
      startPrevY.setHours(0, 0, 0, 0)
      return {
        startCurrent: startCurrentY,
        endCurrent,
        startPrev: startPrevY,
        endPrev: endPrevY,
      }
    case marketingOverviewFilter['YTD']:
      const startCurrentYTD = new Date(today.getFullYear(), 0, 1)
      startCurrentYTD.setHours(0, 0, 0, 0)
      const endCurrentYTD = new Date(today.getFullYear(), 11, 31)
      endCurrentYTD.setHours(23, 59, 59, 999)

      const startPrevYTD = new Date(startCurrentYTD)
      startPrevYTD.setFullYear(startCurrentYTD.getFullYear() - 1)
      const endPrevYTD = new Date(endCurrentYTD)
      endPrevYTD.setFullYear(endCurrentYTD.getFullYear() - 1)

      return {
        startCurrent: startCurrentYTD,
        endCurrent: endCurrentYTD,
        startPrev: startPrevYTD,
        endPrev: endPrevYTD,
      }
    default:
      return {
        startCurrent: today,
        endCurrent: today,
        startPrev: today,
        endPrev: today,
      }
  }
}

export const isDateInRange = (
  dateString: string,
  start: Date,
  end: Date,
  includeEnd: boolean = true,
): boolean => {
  const date = new Date(dateString)
  return date >= start && (includeEnd ? date <= end : date < end)
}

export const formatMarketingDate = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const year = date.getFullYear()
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')
  return `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`
}

export const formatRecurringDays = (daysString: string | undefined): string => {
  if (!daysString) return ''

  const dayMap: { [key: string]: string } = {
    Monday: 'M',
    Tuesday: 'T',
    Wednesday: 'W',
    Thursday: 'Th',
    Friday: 'F',
    Saturday: 'Sa',
    Sunday: 'Su',
  }

  return daysString
    .split(',')
    .map((day) => day.trim())
    .map((day) => dayMap[day] || day)
    .join('-')
}
